// ChildrenKindergartenDetail.jsx
//
// Розподіл дітей ОДНОГО садочка за статтю, наявністю пільги та типом
// труднощів — розгортається з рядка садочка в ChildrenBreakdownTable.jsx.
// На відміну від KindergartenDetail/GroupInlineDetail, тут власний
// API-виклик (statistics_childrens_by_kindergarten з kg_id), бо ці розрізи
// бекенд віддає лише в лениво довантаженому вигляді для одного садочка —
// інакше довелось би тягнути цю деталізацію одразу для всіх 500+ садочків.
import React, { useEffect, useState, useMemo } from 'react';
import { statisticsApi } from './statisticsApi';
import { aggregateDistribution } from './utils/aggregateDistribution';
import ChartDonut from './ChartDonut';

const SEX_COLORS = { 'ч': '#3498db', 'ж': '#e8a898' };
const BENEFIT_COLORS = { yes: '#58d68d', no: '#d1d5db' };
const DISABILITY_COLORS = ['#9b59b6', '#f4d03f', '#e74c3c', '#1abc9c', '#f7dc6f', '#85c1e9', '#c39bd3', '#a9dfbf'];

const ChildrenKindergartenDetail = ({ kgId, kgNumber, district, dataParams, disabilities, onClose }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    statisticsApi.getChildrenDistributionByKindergarten(kgId, dataParams)
      .then(res => {
        if (cancelled) return;
        if (res.data && Array.isArray(res.data)) setRows(res.data);
        else setRows([]);
      })
      .catch(err => { if (!cancelled) setError(err.message || 'Помилка завантаження даних'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kgId, dataParams]);

  const { sexTotals, benefitTotals, disabilityTotals, total } = useMemo(
    () => aggregateDistribution(rows),
    [rows]
  );

  const disabilityLabel = (id) => {
    if (id === 'none') return 'без труднощів';
    const d = disabilities.find(x => x._id === id);
    return d ? (d.label || d.name) : id;
  };

  const disabilityEntries = useMemo(
    () => Object.entries(disabilityTotals).sort((a, b) => b[1] - a[1]),
    [disabilityTotals]
  );

  const sexChartData = {
    labels: ['Хлопчики', 'Дівчатка'],
    datasets: [{ data: [sexTotals['ч'], sexTotals['ж']], backgroundColor: [SEX_COLORS['ч'], SEX_COLORS['ж']], borderWidth: 0 }],
  };
  const benefitChartData = {
    labels: ['З пільгою', 'Без пільги'],
    datasets: [{ data: [benefitTotals.yes, benefitTotals.no], backgroundColor: [BENEFIT_COLORS.yes, BENEFIT_COLORS.no], borderWidth: 0 }],
  };
  const disabilityChartData = {
    labels: disabilityEntries.map(([id]) => disabilityLabel(id)),
    datasets: [{
      data: disabilityEntries.map(([, c]) => c),
      backgroundColor: disabilityEntries.map((_, i) => DISABILITY_COLORS[i % DISABILITY_COLORS.length]),
      borderWidth: 0,
    }],
  };

  return (
    <div style={{ padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1f2937' }}>
            Розподіл дітей ЗДО № {kgNumber}
          </span>
          <span style={{ marginLeft: 8, color: '#6b7280', fontSize: '0.82rem' }}>{district}</span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: '#f1f3f4', border: 'none', borderRadius: '50%',
            width: 30, height: 30, cursor: 'pointer', fontSize: 15,
            color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
          title="Закрити"
        >✕</button>
      </div>

      {loading && <div className="text-center py-4 text-muted" style={{ fontSize: '0.85rem' }}>Завантаження…</div>}
      {error && <div className="text-center py-4 text-danger" style={{ fontSize: '0.85rem' }}>Помилка: {error}</div>}

      {!loading && !error && (
        total > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center' }}>
            <ChartDonut
              title="За статтю"
              total={total}
              data={sexChartData}
              legend={[
                { label: 'Хлопчики', value: sexTotals['ч'], color: SEX_COLORS['ч'] },
                { label: 'Дівчатка', value: sexTotals['ж'], color: SEX_COLORS['ж'] },
              ]}
            />
            <ChartDonut
              title="За наявністю пільги"
              total={total}
              data={benefitChartData}
              legend={[
                { label: 'З пільгою', value: benefitTotals.yes, color: BENEFIT_COLORS.yes },
                { label: 'Без пільги', value: benefitTotals.no, color: BENEFIT_COLORS.no },
              ]}
            />
            <ChartDonut
              title="За типом труднощів"
              total={total}
              data={disabilityChartData}
              legend={disabilityEntries.map(([id, count], i) => ({
                label: disabilityLabel(id),
                value: count,
                color: DISABILITY_COLORS[i % DISABILITY_COLORS.length],
              }))}
            />
          </div>
        ) : (
          <div className="text-center py-4 text-muted" style={{ fontSize: '0.85rem' }}>Немає даних за обраними параметрами</div>
        )
      )}
    </div>
  );
};

export default ChildrenKindergartenDetail;